import { behaviorAnimationToWebAnimation } from "../display-editor/useAnimation.js";
import type {
  RenderNode,
  RuntimeEffect,
} from "../../../util/src/layout-compiler/types.js";
import {
  cancelClipSyncForOwner,
  hasClippedChildrenForOwner,
  registerClipSyncCancelForOwner,
  updateClippedChildrenForAnimatedContainer,
} from "./animatedContainerClipSync.js";

type AnimationPlayEffect = Extract<RuntimeEffect, { type: "animation.play" }>;

function shouldSyncClipForAnimatedContainer(
  effect: AnimationPlayEffect,
  renderNodeByKey: Map<string, RenderNode>,
): boolean {
  const targetNode = renderNodeByKey.get(effect.targetNodeKey);
  if (targetNode?.kind !== "container" || targetNode.layout === "flex") {
    return false;
  }
  return hasClippedChildrenForOwner(effect.targetNodeId, renderNodeByKey);
}

export function playCompiledAnimationEffects(
  effects: RuntimeEffect[] | undefined,
  options: {
    runtimeElementByKey: Map<string, HTMLElement>;
    scale: number;
    renderNodeByKey?: Map<string, RenderNode>;
    canvasElement?: HTMLElement | null;
  },
): void {
  if (!effects?.length) {
    return;
  }
  const animationEffects = effects.filter(
    (effect): effect is AnimationPlayEffect => effect.type === "animation.play",
  );
  for (const effect of animationEffects) {
    const element = options.runtimeElementByKey.get(effect.targetNodeKey);
    if (!element) {
      continue;
    }

    if (effect.restart !== false) {
      element.getAnimations().forEach((animation) => animation.cancel());
    }

    const { keyframes, options: animationOptions } =
      behaviorAnimationToWebAnimation(
        {
          ...effect.payload,
          delayMs: effect.delayMs ?? effect.payload.delayMs,
          durationMs: effect.durationMs ?? effect.payload.durationMs,
          iterationCount: effect.infinite
            ? Infinity
            : (effect.iterationCount ?? effect.payload.iterationCount),
          direction: effect.direction ?? effect.payload.direction,
          fillMode: effect.fillMode ?? effect.payload.fillMode,
        },
        options.scale,
      );

    const animation = element.animate(keyframes, animationOptions);
    const shouldSyncClip =
      options.canvasElement &&
      options.renderNodeByKey &&
      shouldSyncClipForAnimatedContainer(effect, options.renderNodeByKey);
    if (!shouldSyncClip || !options.canvasElement || !options.renderNodeByKey) {
      continue;
    }

    cancelClipSyncForOwner(effect.targetNodeId);
    let rafId: number | null = null;
    let cancelled = false;

    const sync = () => {
      updateClippedChildrenForAnimatedContainer(
        effect.targetNodeId,
        element,
        options.canvasElement!,
        options.scale,
        options.runtimeElementByKey,
        options.renderNodeByKey!,
      );
    };

    const stop = () => {
      cancelled = true;
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    registerClipSyncCancelForOwner(effect.targetNodeId, stop);
    const tick = () => {
      if (cancelled) return;
      sync();
      if (
        animation.playState !== "finished" &&
        animation.playState !== "idle"
      ) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      sync();
      rafId = requestAnimationFrame(tick);
    });
    void animation.finished.finally(() => {
      stop();
      sync();
    });
  }
}
